<template>
  <div class="blog-post-layout">
    <div class="blog-post-container">
      <slot name="image" />

      <article class="blog-post-article">
        <slot name="content" />
        <BlogCTA />
      </article>
    </div>
  </div>
</template>

<script setup></script>

<style lang="scss" scoped>
.blog-post-layout {
  margin-top: 100px;
  font-family: $theme-font;
  min-height: 100vh;
  color: rgba(255, 255, 255, 0.85);
}

.blog-post-container {
  max-width: 1200px;
  margin: 2rem auto;
  padding: 0 2rem;

  @include respond-below(md) {
    padding: 0 1.5rem;
  }

  @include respond-below(sm) {
    padding: 0 1rem;
  }
}

.blog-post-article {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 0 4rem;

  @include respond-below(sm) {
    padding: 1.5rem 0 3rem;
  }
}
</style>

<style lang="scss">
// Global styles for blog content (not scoped)
.blog-content {
  font-family: $theme-font;
  line-height: 1.8;

  section {
    margin-bottom: 3rem;

    @include respond-below(sm) {
      margin-bottom: 2.5rem;
    }

    &.intro {
      font-size: 1.125rem;
      color: rgba(255, 255, 255, 0.8);
      margin-bottom: 3rem;
      padding: 1.5rem;
      background: rgba(255, 255, 255, 0.02);
      border-left: 3px solid #5f87ed;
      border-radius: 4px;

      @include respond-below(sm) {
        font-size: 1rem;
        padding: 1rem;
        margin-bottom: 2rem;
      }

      p {
        margin-bottom: 0;
      }
    }

    &.key-takeaways {
      background: rgba(95, 135, 237, 0.08);
      border: 1px solid rgba(95, 135, 237, 0.25);
      border-radius: 8px;
      padding: 1.5rem 1.5rem 1.5rem 1.75rem;
      margin-bottom: 3rem;

      @include respond-below(sm) {
        padding: 1rem 1rem 1rem 1.25rem;
        margin-bottom: 2rem;
      }

      h2 {
        font-size: 1.25rem;
        margin-top: 0;
        margin-bottom: 1rem;
        color: #5f87ed;
      }

      ul {
        margin: 0;
        padding-left: 1.25rem;

        li {
          margin-bottom: 0.5rem;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.85);

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }

  h2 {
    font-family: $theme-font;
    font-size: 2rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    margin-bottom: 1.25rem;
    margin-top: 3rem;
    line-height: 1.3;

    @include respond-below(sm) {
      font-size: 1.5rem;
      margin-top: 2.5rem;
    }
  }

  h3 {
    font-family: $theme-font;
    font-size: 1.375rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 1rem;
    margin-top: 2rem;
    line-height: 1.4;

    @include respond-below(sm) {
      font-size: 1.125rem;
      margin-top: 1.5rem;
    }
  }

  h4 {
    font-family: $theme-font;
    font-size: 1.125rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
    margin-bottom: 0.75rem;
    margin-top: 1.5rem;

    @include respond-below(sm) {
      font-size: 1rem;
    }
  }

  p {
    font-family: $theme-font;
    font-size: 1rem;
    line-height: 1.8;
    color: rgba(255, 255, 255, 0.75);
    margin-bottom: 1.25rem;

    @include respond-below(sm) {
      font-size: 0.9375rem;
    }
  }

  // Image styling
  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 2rem 0;

    @include respond-below(sm) {
      margin: 1.5rem 0;
      border-radius: 6px;
    }
  }

  // Figure with caption
  figure {
    margin: 2.5rem 0;

    img {
      margin: 0 0 1rem 0;
    }

    figcaption {
      text-align: center;
      font-size: 0.875rem;
      color: rgba(255, 255, 255, 0.5);
      font-style: italic;
      line-height: 1.5;
    }

    &.content-image {
      margin: 2.5rem 0;

      img {
        width: 100%;
        border-radius: 8px;
      }
    }
  }

  // Image comparison layout
  .image-comparison {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin: 2.5rem 0;

    @include respond-below(sm) {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .comparison-image {
      position: relative;
      border-radius: 8px;
      overflow: hidden;
      background: rgba(255, 255, 255, 0.02);

      img {
        margin: 0;
        width: 100%;
        height: auto;
        display: block;
      }

      .image-label {
        position: absolute;
        bottom: 0.75rem;
        left: 0.75rem;
        background: rgba(0, 0, 0, 0.75);
        color: #fff;
        padding: 0.375rem 0.875rem;
        border-radius: 4px;
        font-size: 0.8125rem;
        font-weight: 600;
        backdrop-filter: blur(8px);
      }
    }
  }

  // Lists
  ul,
  ol {
    margin: 1.5rem 0;
    padding-left: 1.75rem;

    @include respond-below(sm) {
      padding-left: 1.5rem;
    }

    li {
      font-family: $theme-font;
      font-size: 1rem;
      line-height: 1.8;
      color: rgba(255, 255, 255, 0.75);
      margin-bottom: 0.875rem;

      @include respond-below(sm) {
        font-size: 0.9375rem;
        margin-bottom: 0.75rem;
      }

      strong {
        color: rgba(255, 255, 255, 0.95);
        font-weight: 600;
      }

      // Nested lists
      ul,
      ol {
        margin-top: 0.75rem;
        margin-bottom: 0.75rem;
      }
    }
  }

  ul {
    list-style-type: disc;

    li::marker {
      color: #5f87ed;
    }
  }

  ol {
    list-style-type: decimal;

    li::marker {
      color: #5f87ed;
      font-weight: 600;
    }
  }

  // Links
  a {
    color: #5f87ed;
    text-decoration: none;
    border-bottom: 1px solid rgba(95, 135, 237, 0.3);
    transition: all 0.2s ease;

    &:hover {
      color: #7fa3f0;
      border-bottom-color: #7fa3f0;
    }
  }

  // Blockquotes
  blockquote {
    margin: 2rem 0;
    padding: 1.5rem 1.5rem 1.5rem 2rem;
    background: rgba(95, 135, 237, 0.05);
    border-left: 4px solid #5f87ed;
    border-radius: 4px;

    @include respond-below(sm) {
      padding: 1rem 1rem 1rem 1.5rem;
      margin: 1.5rem 0;
    }

    p {
      color: rgba(255, 255, 255, 0.85);
      font-style: italic;
      margin-bottom: 0;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  // Code blocks
  code {
    font-family: "Monaco", "Courier New", monospace;
    font-size: 0.875rem;
    background: rgba(255, 255, 255, 0.05);
    padding: 0.2rem 0.5rem;
    border-radius: 3px;
    color: #7fa3f0;
  }

  pre {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 1.5rem;
    margin: 2rem 0;
    overflow-x: auto;

    @include respond-below(sm) {
      padding: 1rem;
      margin: 1.5rem 0;
      border-radius: 6px;
    }

    code {
      background: none;
      padding: 0;
      font-size: 0.875rem;
      line-height: 1.6;
    }
  }

  // Tables
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 2rem 0;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
    overflow: hidden;

    @include respond-below(sm) {
      font-size: 0.875rem;
      margin: 1.5rem 0;
    }

    thead {
      background: rgba(95, 135, 237, 0.1);

      th {
        padding: 1rem;
        text-align: left;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.95);
        border-bottom: 2px solid rgba(95, 135, 237, 0.3);

        @include respond-below(sm) {
          padding: 0.75rem 0.5rem;
        }
      }
    }

    tbody {
      tr {
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background: rgba(255, 255, 255, 0.02);
        }
      }

      td {
        padding: 1rem;
        color: rgba(255, 255, 255, 0.75);

        @include respond-below(sm) {
          padding: 0.75rem 0.5rem;
        }
      }
    }
  }

  // Horizontal rule
  hr {
    border: none;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
    margin: 3rem 0;

    @include respond-below(sm) {
      margin: 2rem 0;
    }
  }

  // Info boxes / callouts
  .info-box,
  .tip-box,
  .warning-box {
    padding: 1.5rem;
    border-radius: 8px;
    margin: 2.5rem 0;
    background: rgba(95, 135, 237, 0.08);
    border: 1px solid rgba(95, 135, 237, 0.2);

    @include respond-below(sm) {
      padding: 1.25rem;
      margin: 2rem 0;
      border-radius: 6px;
    }

    h4 {
      margin-top: 0;
      margin-bottom: 1rem;
      color: rgba(255, 255, 255, 0.95);
      font-size: 1rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    p {
      margin-bottom: 1rem;
      color: rgba(255, 255, 255, 0.8);

      &:last-child {
        margin-bottom: 0;
      }
    }

    ul,
    ol {
      margin-top: 1rem;
      margin-bottom: 0;
    }
  }

  .info-box {
    background: rgba(95, 135, 237, 0.08);
    border-color: rgba(95, 135, 237, 0.2);
  }

  .tip-box {
    background: rgba(76, 175, 80, 0.08);
    border-color: rgba(76, 175, 80, 0.2);
  }

  .warning-box {
    background: rgba(255, 152, 0, 0.08);
    border-color: rgba(255, 152, 0, 0.2);
  }

  // Feature highlights
  .feature-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;

    @include respond-below(sm) {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .feature-item {
      padding: 1.5rem;
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 8px;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.04);
        border-color: rgba(255, 255, 255, 0.15);
      }

      h4 {
        margin-top: 0;
      }
    }
  }

  // Step indicators for tutorials
  .step-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    background: #5f87ed;
    color: #fff;
    border-radius: 50%;
    font-weight: 600;
    font-size: 0.875rem;
    margin-right: 0.75rem;
  }
}
</style>
